import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 10: MCP Security Scanning Report
 * Large-scale audit of 500 MCP server packages with an LLM Jury pipeline on SageMaker.
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 160,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 160,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 180,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 190,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 160,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project10FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups
      {
        id: "bg-ingest",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 620,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-scan",
        position: { x: 640, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 620,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-jury",
        position: { x: 1240, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 620,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-report",
        position: { x: 1840, y: 40 },
        data: { label: "" },
        style: {
          width: 460,
          height: 620,
          background: "#f5f3ff40",
          border: "3px solid #c4b5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-ingest", position: { x: 200, y: 50 }, data: { label: "Ingestion (500 MCP pkgs)" }, style: nodeStyle.section },
      { id: "sec-scan", position: { x: 790, y: 50 }, data: { label: "Static Scanners" }, style: nodeStyle.section },
      { id: "sec-jury", position: { x: 1390, y: 50 }, data: { label: "LLM Jury (SageMaker)" }, style: nodeStyle.section },
      { id: "sec-report", position: { x: 1960, y: 50 }, data: { label: "Findings & Report" }, style: nodeStyle.section },

      // Ingestion path
      { id: "i1", position: { x: 200, y: 140 }, data: { label: "GitHub / npm / PyPI" }, style: nodeStyle.compute },
      { id: "i2", position: { x: 200, y: 230 }, data: { label: "Crawler (Python)" }, style: nodeStyle.ops },
      { id: "i3", position: { x: 200, y: 320 }, data: { label: "S3: raw packages" }, style: nodeStyle.data },
      { id: "i4", position: { x: 200, y: 410 }, data: { label: "Manifest + Metadata" }, style: nodeStyle.data },
      { id: "i5", position: { x: 200, y: 510 }, data: { label: "Step Functions" }, style: nodeStyle.net },

      // Scanners column
      { id: "s1", position: { x: 800, y: 140 }, data: { label: "AST / Code Scanner" }, style: nodeStyle.ops },
      { id: "s2", position: { x: 800, y: 230 }, data: { label: "Secret / Credential Scan" }, style: nodeStyle.ops },
      { id: "s3", position: { x: 800, y: 320 }, data: { label: "Tool-Poisoning Heuristics" }, style: nodeStyle.ops },
      { id: "s4", position: { x: 800, y: 410 }, data: { label: "Transport Security Check" }, style: nodeStyle.ops },
      { id: "s5", position: { x: 800, y: 510 }, data: { label: "Access-Control Audit" }, style: nodeStyle.ops },
      { id: "s6", position: { x: 800, y: 600 }, data: { label: "S3: raw findings" }, style: nodeStyle.data },

      // LLM Jury column
      { id: "j1", position: { x: 1390, y: 140 }, data: { label: "SageMaker Endpoint" }, style: nodeStyle.compute },
      { id: "j2", position: { x: 1290, y: 240 }, data: { label: "Worker LLM A" }, style: nodeStyle.ops },
      { id: "j3", position: { x: 1490, y: 240 }, data: { label: "Worker LLM B" }, style: nodeStyle.ops },
      { id: "j4", position: { x: 1390, y: 340 }, data: { label: "Worker LLM C" }, style: nodeStyle.ops },
      { id: "j5", position: { x: 1390, y: 440 }, data: { label: "Judge LLM (aggregator)" }, style: nodeStyle.note },
      { id: "j6", position: { x: 1390, y: 540 }, data: { label: "Clustering / Dedup" }, style: nodeStyle.ops },
      { id: "j7", position: { x: 1390, y: 630 }, data: { label: "DynamoDB: verdicts" }, style: nodeStyle.data },

      // Report column
      { id: "r1", position: { x: 1880, y: 140 }, data: { label: "CWE Mapping" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "r2", position: { x: 1880, y: 240 }, data: { label: "Severity Distribution" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "r3", position: { x: 1880, y: 340 }, data: { label: "Remediation Guidance" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "r4", position: { x: 1880, y: 440 }, data: { label: "Vulnerability Patterns" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "r5", position: { x: 1880, y: 540 }, data: { label: "Final Security Report" }, style: { ...nodeStyle.compute, width: 200 } },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };
    const purple = { stroke: "#7c3aed" };

    return [
      // Ingestion
      mkEdge("ig1", "i1", "i2", "discover", { style: { ...blue } }),
      mkEdge("ig2", "i2", "i3", "download", { style: { ...blue } }),
      mkEdge("ig3", "i2", "i4", "extract meta", { style: { ...blue } }),
      mkEdge("ig4", "i3", "i5", "fan-out", { style: { ...blue, strokeDasharray: "10 8" } }),

      // Ingestion -> Scanners
      mkEdge("is1", "i5", "s1", "dispatch", { style: { ...green } }),
      mkEdge("is2", "i5", "s2", "dispatch", { style: { ...green } }),
      mkEdge("is3", "i5", "s3", "dispatch", { style: { ...green } }),
      mkEdge("is4", "i5", "s4", "dispatch", { style: { ...green } }),
      mkEdge("is5", "i5", "s5", "dispatch", { style: { ...green } }),

      // Scanners -> findings
      mkEdge("sf1", "s1", "s6", "emit", { style: { ...green } }),
      mkEdge("sf2", "s2", "s6", "emit", { style: { ...green } }),
      mkEdge("sf3", "s3", "s6", "emit", { style: { ...green } }),
      mkEdge("sf4", "s4", "s6", "emit", { style: { ...green } }),
      mkEdge("sf5", "s5", "s6", "emit", { style: { ...green } }),

      // Findings -> Jury
      mkEdge("sj1", "s6", "j1", "invoke", { style: { ...orange } }),

      // Jury internal
      mkEdge("jj1", "j1", "j2", "shard", { style: { ...orange } }),
      mkEdge("jj2", "j1", "j3", "shard", { style: { ...orange } }),
      mkEdge("jj3", "j1", "j4", "shard", { style: { ...orange } }),
      mkEdge("jj4", "j2", "j5", "label", { style: { ...orange } }),
      mkEdge("jj5", "j3", "j5", "label", { style: { ...orange } }),
      mkEdge("jj6", "j4", "j5", "label", { style: { ...orange } }),
      mkEdge("jj7", "j5", "j6", "merge", { style: { ...orange } }),
      mkEdge("jj8", "j6", "j7", "persist", { style: { ...orange } }),

      // Jury -> Report
      mkEdge("jr1", "j7", "r1", "map CWE", { style: { ...purple } }),
      mkEdge("jr2", "j7", "r2", "severity", { style: { ...purple } }),
      mkEdge("jr3", "j7", "r3", "remediation", { style: { ...purple } }),
      mkEdge("jr4", "j6", "r4", "clusters", { style: { ...purple } }),
      mkEdge("jr5", "r1", "r5", "compile", { style: { ...purple } }),
      mkEdge("jr6", "r2", "r5", "compile", { style: { ...purple } }),
      mkEdge("jr7", "r3", "r5", "compile", { style: { ...purple } }),
      mkEdge("jr8", "r4", "r5", "compile", { style: { ...purple } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
